/**
 * Component for displaying a Summary  modal with detailed candidate information.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {boolean} props.open - Indicates whether the modal is open or not.
 * @param {function} props.setOpen - Function to toggle the modal's open state.
 * @param {Object} props.record - Object containing data about the candidate.
 * @param {string} [props.record.Summary] - Candidate summary with optional highlighted text separated by '**'.
 * @param {string} [props.record.Percentiles] - JSON string containing percentile data for various categories.
 * @param {string} [props.record.Name] - Candidate's name.
 * @param {number} [props.record.Weighted_Score] - Weighted score of the candidate (0-100).
 * @param {number} [props.record.Rank] - Candidate's rank among all candidates.
 * @param {Array<string>} [props.record.returnedCriteria] - List of criteria returned for the candidate.
 *
 * @returns {JSX.Element} A modal component displaying the candidate's details, scores, and other information.
 *
 * @example
 * <ResultsModal
 *   open={true}
 *   setOpen={toggleModal}
 *   record={{
 *     Summary: "Candidate summary text**Highlighted text",
 *     Percentiles: '{"Category1": 85, "Category2": 70}',
 *     Name: "John Doe",
 *     Weighted_Score: 92.5,
 *     Rank: 1,
 *     returnedCriteria: ["Criteria1", "Criteria2"],
 *   }}
 * />
 */


import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, Button, Grid, LinearProgress, Modal, Typography} from '@mui/material'
import StarIcon from '@mui/icons-material/Star';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import './PreScreeningModal.css';
import {message} from 'antd';


import {Doughnut} from 'react-chartjs-2';
import {ArcElement, Chart as ChartJS, Tooltip} from 'chart.js';
import rxLogo from './rx_logo_all.png';
import gradientImage from './RX_Gradient.png';

ChartJS.register(ArcElement, Tooltip);

const ResultsModal = ({ open, setOpen, record, backendURL, getCookie }) => {
    console.log("Opening summary for record:", record)
    const [percentilesData, setPercentilesData] = useState(null);

    useEffect(() => {
        if (open && record?.Percentiles) {
            setPercentilesData(JSON.parse(record.Percentiles));
        }
    }, [open, record]);

    const closeModal = useCallback(() => setOpen(false), [setOpen]);

    const summaryParts = useMemo(() => record?.Summary?.split('**') || ['No summary is available', 'No summary is available'], [record]);
    const [summaryText, highlightedText] = summaryParts;

    const formatPriorityName = (priority) => {
        if (!priority || priority === 'default') return '';
        return priority.replace(/_/g, ' ');
    };

    const hasPriority = useMemo(() => {
        return record?.priority1 && record.priority1 !== 'default';
    }, [record]);

    const modalStyle = useMemo(
        () => ({
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '95%',
            maxWidth: '1000px',
            maxHeight: '900px',
            height: '90vh',
            bgcolor: '#f0f0f0',
            borderRadius: 2,
            boxShadow: 3,
            p: 4,
            display: 'flex',
            flexDirection: 'column',
        }),
        []
    );
    const [downloadOptions, setDownloadOptions] = useState({
        includeRank: true,
        includeCV: false,
        includeAdditionalCriteria: false
    });


    const [isButtonVisible, setIsButtonVisible] = useState(true);

    const handleDownload = () => {
        fetch(`${backendURL}/v1/download-report`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ...record,
                includeRank: downloadOptions.includeRank,
                includeCV: downloadOptions.includeCV,
                includeAdditionalCriteria: downloadOptions.includeAdditionalCriteria,
                jobID: record.JobID || record.jobID,  // Make sure jobID is passed
                userID: getCookie ? getCookie() : ''  // Pass userID for CV retrieval
            })
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.blob();
            })
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const extension = downloadOptions.includeCV ? 'zip' : 'pdf';
                a.download = `candidate_report_${record.Name.replace(/\s+/g, '_')}.${extension}`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            })
            .catch(error => {
                console.error('Error downloading PDF report:', error);
                message.error('Failed to download report');
            });
    };




    const candidateNameContainerStyle = {
        backgroundColor: `#F3E8F8`,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        padding: '16px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '5px',
    };

    const getBarColor = (value) => {
        /**
         * Determines the colour of a progress bar based on a given value.
         *
         * The `getBarColor` function transitions the bar colour from red to yellow when the value is less than 50,
         * and from yellow to darker green when the value is greater than or equal to 50.
         *
         * @param {number} value - A numeric value (0-100) representing the progress or score.
         * @returns {string} A string in `rgb(red, green, blue)` format representing the calculated colour.
         *
         * @example
         * getBarColor(25); // Output: "rgb(255, 127, 0)" (Orange)
         * getBarColor(50); // Output: "rgb(255, 255, 0)" (Yellow)
         * getBarColor(75); // Output: "rgb(127, 191, 0)" (Greenish yellow)
         *
         * @description
         * - **For values < 50**: The colour transitions from red (255, 0, 0) to yellow (255, 255, 0).
         *   - Red is fixed at 255, while green is calculated as `(value / 50) * 255`.
         * - **For values >= 50**: The colour transitions from yellow (255, 255, 0) to darker green (0, 127, 0).
         *   - Red decreases as `(255 - ((value - 50) / 50) * 255)`.
         *   - Green decreases as `(255 - ((value - 50) / 50) * 127)`.
         *
         * - Returns an RGB string with the calculated `red`, `green`, and a fixed `blue` value of 0.
         *
         * @note
         * - The `console.log` statements are used for debugging purposes:
         *   - Logs the candidate's rank (`record.Rank`).
         *   - Logs the full dataset of candidates (`record.allCandidatesData`).
         */

        let red, green;
        console.log(record.jobTitle);
        console.log("all candiates", record.allCandidatesData)
        if (value < 50) {
            // Transition from red to yellow
            red = 255;
            green = Math.round((value / 50) * 255);
        } else {
            // Transition from yellow to darker green
            red = Math.round(255 - ((value - 50) / 50) * 255);
            green = Math.round(255 - ((value - 50) / 50) * 127);
        }
        return `rgb(${red}, ${green}, 0)`;
    };

    const hasValidCriteria = useMemo(() => {
        /**
         * A memoized computation to check if the candidate record contains valid criteria.
         *
         * This function evaluates the `returnedCriteria` array from the `record` prop
         * to determine if at least one criterion is non-empty after trimming whitespace.
         *
         * @returns {boolean} `true` if there is at least one valid, non-empty criterion; otherwise, `false`.
         *
         * @example
         * // Example with valid criteria
         * const record = { returnedCriteria: ["Criteria1", "  ", "Criteria3"] };
         * const hasValidCriteria = useMemo(() => {
         *   return record?.returnedCriteria?.some((criteria) => criteria.trim() !== "");
         * }, [record]);
         * console.log(hasValidCriteria); // Output: true
         *
         * @example
         * // Example with no valid criteria
         * const record = { returnedCriteria: ["  ", ""] };
         * const hasValidCriteria = useMemo(() => {
         *   return record?.returnedCriteria?.some((criteria) => criteria.trim() !== "");
         * }, [record]);
         * console.log(hasValidCriteria); // Output: false
         */

        return record?.returnedCriteria?.some((criteria) => criteria.trim() !== "");
    }, [record]);

    const gaugeData = {
        /**
         * Configuration object for the gauge chart representing the candidate's weighted score.
         *
         * The `gaugeData` object is used to define the appearance and data for a semi-circular gauge chart,
         * which visualises the candidate's weighted score as a portion of 100%.
         *
         * @type {Object}
         * @property {Array<Object>} datasets - An array of dataset objects for the chart.
         * @property {Array<number>} datasets[].data - Data array containing the candidate's score and the remaining value (to complete 100%).
         * @property {Array<string>} datasets[].backgroundColor - Array specifying the colour for the filled part of the gauge and the transparent remainder.
         * @property {number} datasets[].borderWidth - Sets the border width of the chart segments (0 for no borders).
         * @property {number} datasets[].circumference - Specifies the angular extent of the chart in degrees (180 for a semi-circle).
         * @property {number} datasets[].rotation - Starting angle for the chart (-90 to begin at the bottom).
         * @property {string} datasets[].cutout - Defines the inner radius of the chart as a percentage of the chart radius ('75%' for a gauge style).
         *
         * @example
         * const gaugeData = {
         *     datasets: [
         *         {
         *             data: [85, 15], // Candidate scored 85%, remainder is 15%.
         *             backgroundColor: ['#4CAF50', 'rgba(0, 0, 0, 0)'],
         *             borderWidth: 0,
         *             circumference: 180,
         *             rotation: -90,
         *             cutout: '75%',
         *         },
         *     ],
         * };
         */

        datasets: [
            {
                data: [record.Weighted_Score, 100 - record.Weighted_Score],
                backgroundColor: ['#4CAF50', 'rgba(0, 0, 0, 0)'],
                borderWidth: 0,
                circumference: 180,
                rotation: -90,
                cutout: '75%',
            },
        ],
    };

    const gaugeOptions = {
        /**
         * Configuration object for the gauge chart options.
         *
         * The `gaugeOptions` object defines the behaviour and appearance of the weighted score gauge chart,
         *
         * @type {Object}
         * @property {number} rotation - Starting angle of the chart (-90 to begin at the bottom).
         * @property {number} circumference - Angular extent of the chart in degrees (180 for a semi-circle).
         * @property {string} cutout - Inner radius of the chart as a percentage of the chart's total radius ('75%' for a gauge style).
         * @property {Object} plugins - Configuration for chart plugins.
         * @property {Object} plugins.tooltip - Tooltip configuration.
         * @property {boolean} plugins.tooltip.enabled - Disables tooltips when set to `false`.
         * @property {Array} events - Array of event types; an empty array disables all interactions.
         *
         * @example
         * const gaugeOptions = {
         *     rotation: -90,
         *     circumference: 180,
         *     cutout: '75%',
         *     plugins: {
         *         tooltip: { enabled: false },
         *     },
         *     events: [], // No interactions allowed
         * };
         */

        rotation: -90,
        circumference: 180,
        cutout: '75%',
        plugins: {
            tooltip: { enabled: false },
        },
        events: [], // Disable all interactions
    };

    const renderDoughnutText = {
        /**
         * Plugin configuration for rendering custom text inside a doughnut chart.
         *
         * The `renderDoughnutText` plugin draws the candidate's weighted score as text in the centre of the gauge chart
         *
         * @type {Object}
         * @property {string} id - A unique identifier for the plugin.
         * @property {function} beforeDraw - Hook executed before the chart is drawn, used to render custom text.
         * @param {Object} chart - The chart instance passed to the `beforeDraw` function.
         * @param {Object} chart.ctx - The canvas rendering context used to draw on the chart.
         * @param {number} chart.width - The width of the chart.
         * @param {number} chart.height - The height of the chart.
         *
         */

        id: 'renderDoughnutText',
        beforeDraw: (chart) => {
            const { width, height, ctx } = chart;
            ctx.restore();
            const fontSize = (height / 150).toFixed(2);
            ctx.font = `${fontSize}em sans-serif`;
            ctx.textBaseline = 'middle';

            const text = `${record.Weighted_Score.toFixed(1)}`;
            const textX = Math.round((width - ctx.measureText(text).width) / 2);
            const textY = height / 1.6;

            ctx.fillText(text, textX, textY);
            ctx.save();
        },
    };

    return (
        <Modal
            open={open}
            onClose={closeModal}
            destroyOnClose={true}
            aria-labelledby="modal-title"
            BackdropProps={{
                style: {
                    backdropFilter: 'blur(3px)',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                },
            }}
        >
            <Box id="modal-content" sx={modalStyle}>
                {/* RX Logo */}
                <Box sx={candidateNameContainerStyle}>
                    {/* RX Logo aligned to the left */}
                    <Box sx={{ position: 'absolute', left: '46px' }}>
                        <img src={rxLogo} alt="RecX Logo" style={{ width: '120px', height: 'auto' }} />
                    </Box>
                    {/* Candidate Name centered */}
                    <Typography variant="h5" sx={{ color: 'black', fontWeight: 600 }}>
                        {record.Name}
                    </Typography>
                </Box>

                <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems="center" justifyContent="space-around" mb={0} sx={{ width: '100%', gap: 2, flex: 0.3 }}>
                    <Box display="flex" flex={0.7} flexDirection="column" alignItems="center">
                        <Typography variant="h6" sx={{ color: 'primary.dark', fontWeight: 600 }}>
                            Overall Score
                        </Typography>
                    </Box>
                    <Box display="flex" flex={0.7} flexDirection="column" alignItems="center">
                        <Typography variant="h6" sx={{ color: 'primary.dark', fontWeight: 600 }}>
                            Rank
                        </Typography>
                    </Box>
                </Box>

                <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems="center" justifyContent="space-around" mb={0} mt={0} sx={{ width: '100%', gap: 2, flex: 0.5 }}>
                    <Box display="flex" flex={0.7} flexDirection="column" alignItems="center">
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                flex: 0.7,
                                width: '100%',
                                maxWidth: '200px',
                                height: 'auto',
                                maxHeight: '150px',
                            }}
                        >
                            <Doughnut data={gaugeData} options={gaugeOptions} plugins={[renderDoughnutText]} />
                        </Box>
                    </Box>

                    {/* Rank Badge */}
                    <Box display="flex" flex={0.7} flexDirection="column" alignItems="center">
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                width: '100px', // Enlarged the circle
                                height: '100px', // Enlarged the circle
                                borderRadius: '50%',
                                bgcolor: 'gold',
                                boxShadow: '0px 4px 10px rgba(0,0,0,0.3)',
                                position: 'relative',
                            }}
                        >
                            {/* Rank Number */}
                            <Typography
                                variant="h4"
                                sx={{
                                    color: '#000',
                                    fontWeight: 600,
                                    textAlign: 'center',
                                    lineHeight: 1, // To help center the text vertically
                                }}
                            >
                                {record.Rank}
                            </Typography>
                            {/* Total Candidates */}
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#000',
                                    fontWeight: 400,
                                    textAlign: 'center',
                                    marginTop: '4px',
                                    fontSize: '0.95rem',
                                }}
                            >
                                of {record.allCandidatesData ? record.allCandidatesData.length : 'N/A'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {hasPriority && (
                    <Box
                        sx={{
                            bgcolor: '#e3f2fd',
                            borderRadius: 1.5,
                            p: 1,
                            mb: 1,
                        }}
                    >
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                fontWeight: 'bold',
                                color: '#005691',
                                fontSize: '0.85rem',
                                mb: 0.5 
                            }}
                        >
                            Evaluation Focus:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            {record.priority1 && record.priority1 !== 'default' && (
                                <Typography variant="caption" sx={{ color: '#555', ml: 1, fontSize: '0.75rem', lineHeight: 1.3 }}>
                                    Primary: {formatPriorityName(record.priority1)}
                                </Typography>
                            )}
                            {record.priority2 && record.priority2 !== 'default' && (
                                <Typography variant="caption" sx={{ color: '#555', ml: 1, fontSize: '0.75rem', lineHeight: 1.3 }}>
                                    Secondary: {formatPriorityName(record.priority2)}
                                </Typography>
                            )}
                            {record.priority3 && record.priority3 !== 'default' && (
                                <Typography variant="caption" sx={{ color: '#555', ml: 1, fontSize: '0.75rem', lineHeight: 1.3 }}>
                                    Tertiary: {formatPriorityName(record.priority3)}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                <Box display="flex" id="summary-container" flexDirection={{ xs: 'column', md: 'row' }} gap={2} flexGrow={1} overflow="hidden">
                    <Box flex={1} display="flex" flexDirection="column" gap={2} overflow="hidden">
                        <Box
                            flexGrow={1}
                            overflow="auto"
                            bgcolor="#ffffff"
                            borderRadius={2}
                            p={3}
                            sx={{ boxShadow: 0 }}
                        >
                            <Typography variant="body1" sx={{ color: '#000', fontSize: '1rem' }}>
                                {summaryText}
                            </Typography>
                        </Box>
                        {highlightedText && (
                            <Box
                                sx={{
                                    backgroundColor: 'rgba(255, 223, 186, 0.5)',
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 2,
                                    mt: 2,
                                }}
                            >
                                <StarIcon sx={{ color: 'warning.main', mr: 1, animation: 'pulse 1.5s infinite ease-in-out' }} />
                                <Typography variant="body1" sx={{ color: '#000', fontSize: '1rem' }}>
                                    {highlightedText}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    {percentilesData && (
                        <Box flex={1} bgcolor="rgba(220, 240, 255, 0.5)" borderRadius={2} p={1} display="flex" flexDirection="column" height="100%" overflow="auto">
                            <Typography variant="h6" sx={{ color: 'black', fontWeight: 600, fontSize: '0.8rem', mb:1 }}>
                                Candidate performs better than x % of candidates
                            </Typography>
                            <Grid container spacing={2}>
                                {Object.entries(percentilesData).map(([category, value]) => (
                                    <React.Fragment key={category}>
                                        <Grid item xs={4} sx={{ mb: 2 }}>
                                            <Typography variant="body2" sx={{ color: 'primary.dark', fontWeight: 600, fontSize: '0.8rem' }}>
                                                {category.replace(/_/g, ' ')}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={2} sx={{ mb: 2 }}>
                                            <Typography variant="caption" sx={{ color: '#000', fontSize: '0.9rem' }}>
                                                {Math.round(value)}%
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6} sx={{ mb: 2 }}>
                                            <Box sx={{ position: 'relative', width: '100%' }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={value}
                                                    sx={{
                                                        height: 10,
                                                        borderRadius: 1,
                                                        boxShadow: '0px 3px 5px rgba(0, 0, 0, 0.2)',
                                                        '& .MuiLinearProgress-bar': {
                                                            backgroundColor: getBarColor(value),
                                                        },
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                    </React.Fragment>
                                ))}
                            </Grid>
                        </Box>
                    )}
                </Box>

                <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" mt={2} >
                    <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        gap={1}
                        bgcolor={hasValidCriteria ? "#ffffff" : "#f0f0f0"}
                        p={1}
                        mr={2}
                        borderRadius={2}
                        sx={{ width: '90%' }}
                    >
                        {hasValidCriteria &&
                            record.returnedCriteria.map((criteria, index) => {
                                /**
                                 * Dynamically renders a list of criteria with their statuses.
                                 *
                                 * This block iterates through the `returnedCriteria` array, checking each criterion's value
                                 * from the `record` object and displaying its name along with a status icon (check or cross).
                                 * Empty or invalid criteria are skipped.
                                 *
                                 * @param {boolean} hasValidCriteria - Indicates if there are valid criteria to display.
                                 * @param {Object} record - Object containing criteria information.
                                 * @param {Array<string>} record.returnedCriteria - Array of criteria names.
                                 * @param {string} record.CriteriaN - Status value of each criterion, where `N` corresponds to the index + 1.
                                 *
                                 * @returns {JSX.Element|null} A list of criteria with their statuses, or `null` if no valid criteria exist.
                                 */

                                if (!criteria.trim()) return null;
                                const criteriaValue = record[`Criteria${index + 1}`];
                                return (
                                    <Box key={index} display="flex" alignItems="center" gap={0.5}>
                                        <Typography variant="body2" sx={{ color: 'primary.dark', fontWeight: 600, fontSize: '1rem' }}>
                                            {criteria}:
                                        </Typography>
                                        {
                                            /*
                                            Add more case checking here, true false.lowercase, and else
                                            Gray dash
                                             */
                                        }

                                        {criteriaValue === 'true' ? (
                                            <CheckIcon sx={{ color: 'success.main', fontSize: '1.5rem' }} />
                                            //need to add more error checking here
                                        ) : (
                                            <CloseIcon sx={{ color: 'error.main', fontSize: '1.5rem' }} />
                                        )}
                                    </Box>
                                );
                            })
                        }
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center"  gap={1}>
                        {isButtonVisible && (
                            <>
                            {/* Download Options */}
                            <Box sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center',
                                padding: '8px 12px',
                                backgroundColor: '#f5f5f5',
                                borderRadius: 1,
                                marginRight: 2
                            }}>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={downloadOptions.includeRank}
                                        onChange={(e) => setDownloadOptions({
                                            ...downloadOptions,
                                            includeRank: e.target.checked
                                        })}
                                        style={{ marginRight: '5px' }}
                                    />
                                    <Typography variant="body2">Include Rank</Typography>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={downloadOptions.includeCV}
                                        onChange={(e) => setDownloadOptions({
                                            ...downloadOptions,
                                            includeCV: e.target.checked
                                        })}
                                        style={{ marginRight: '5px' }}
                                    />
                                    <Typography variant="body2">Include CV</Typography>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={downloadOptions.includeAdditionalCriteria}
                                        onChange={(e) => setDownloadOptions({
                                            ...downloadOptions,
                                            includeAdditionalCriteria: e.target.checked
                                        })}
                                        style={{ marginRight: '5px' }}
                                    />
                                    <Typography variant="body2">Include Additional Criteria</Typography>
                                </label>
                            </Box>
                            <Button
                                onClick={handleDownload}
                                sx={{
                                    bgcolor: 'primary.main',
                                    color: '#fff',
                                    textTransform: 'none',
                                    px: 3,
                                    py: 1,
                                    borderRadius: 1,
                                    '&:hover': {
                                        bgcolor: 'primary.dark',
                                    },
                                }}
                            >
                                Download
                            </Button>
                            </>
                        )}
                        <Button
                            onClick={closeModal}
                            sx={{
                                bgcolor: 'error.main',
                                color: '#fff',
                                textTransform: 'none',
                                px: 3,
                                py: 1,
                                borderRadius: 1,
                                '&:hover': {
                                    bgcolor: 'error.dark',
                                },
                            }}
                        >
                            Close
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Modal>

    );
};

ResultsModal.propTypes = {
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    record: PropTypes.shape({
        Summary: PropTypes.string,
        Percentiles: PropTypes.string,
        Name: PropTypes.string,
        Weighted_Score: PropTypes.number,
        Rank: PropTypes.number,
        returnedCriteria: PropTypes.arrayOf(PropTypes.string),
        priority1: PropTypes.string,
        priority2: PropTypes.string,
        priority3: PropTypes.string,
    }).isRequired,
};

export default ResultsModal;
